import React, { type ReactElement, useMemo } from 'react'
import { PrimaryButton } from '../../components/Button'
import { InputLabel, OptionalLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import PDFFileUpload from './PDFFileUpload'
import Tooltip from '../../components/Tooltip'
import { highlightInvalidFields } from '../../util/Util'
import { AccountServicesType, MAX_ADDITIONAL_DOCUMENTS } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'

export interface AdditionalDocumentFormItem {
  file?: string
  url?: string
  fileObj?: File
  fileName?: string
}

interface IProps {
  state: {
    formationDocument?: string
    formationDocumentUrl?: string
    formationDocumentFile?: File
    lastUploadedFormationDocumentFileName?: string
    ownershipDocument?: string
    ownershipDocumentUrl?: string
    ownershipDocumentFile?: File
    lastUploadedOwnershipDocumentFileName?: string
    activityDocument?: string
    activityDocumentUrl?: string
    activityDocumentFile?: File
    lastUploadedActivityDocumentFileName?: string
    formationOrOwnershipDocument?: string
    formationOrOwnershipDocumentUrl?: string
    formationOrOwnershipDocumentFile?: File
    lastUploadedFormationOrOwnershipDocumentFileName?: string
    additionalDocuments?: AdditionalDocumentFormItem[]
  }
  isSubmitting: boolean
  onUpdate: (value: Partial<IProps['state']>) => void
  onSubmit: () => void
  accountType: AccountServicesType
  getError: (fieldId: string) => string | undefined
}

const Documents = ({ state, isSubmitting, onUpdate, onSubmit, accountType, getError }: IProps): ReactElement => {
  const additionalDocuments = state.additionalDocuments ?? []

  const isValid = useMemo(() => {
    const isCryptoFiat = accountType === AccountServicesType.crypto_and_fiat

    return isCryptoFiat
      ? (!!state.formationDocument || !!state.formationDocumentUrl || !!state.formationDocumentFile) &&
        (!!state.ownershipDocument || !!state.ownershipDocumentUrl || !!state.ownershipDocumentFile) &&
        (!!state.activityDocument || !!state.activityDocumentUrl || !!state.activityDocumentFile)
      : !!state.formationOrOwnershipDocument ||
        !!state.formationOrOwnershipDocumentUrl ||
        !!state.formationOrOwnershipDocumentFile
  }, [state, accountType])

  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14, paddingBottom: 30 }}>
    <div style={{ marginBottom: 20 }}>
      <PageHeader title="Document submission" subTitle="Upload required documents for verification" />
    </div>
    { accountType === AccountServicesType.crypto_and_fiat
      ? <>    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>
          Formation document
          </InputLabel>
          <OptionalLabel>PDF</OptionalLabel>
          <Tooltip message='Upload your certificate of formation or incorporation' />
        </div>
        <VerificationRequirementError message={getError('formationDocument')} />
        <PDFFileUpload
          file={state.formationDocumentUrl ?? state.formationDocumentFile}
          onFile={f => onUpdate({ formationDocumentFile: f })}
          onRemove={() => {
            onUpdate({
              formationDocument: '',
              formationDocumentUrl: undefined,
              formationDocumentFile: undefined,
              lastUploadedFormationDocumentFileName: undefined
            })
          }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>
          Ownership document
          </InputLabel>
          <OptionalLabel>PDF</OptionalLabel>
          <Tooltip message='Provide a signed document from a verified control person or regulatory authority listing all shareholders and their ownership percentages, ensuring the full 100% ownership is clearly accounted for.' />
        </div>
        <VerificationRequirementError message={getError('ownershipDocument')} />
        <PDFFileUpload
          file={state.ownershipDocumentUrl ?? state.ownershipDocumentFile}
          onFile={f => onUpdate({ ownershipDocumentFile: f })}
          onRemove={() => {
            onUpdate({
              ownershipDocument: '',
              ownershipDocumentUrl: undefined,
              ownershipDocumentFile: undefined,
              lastUploadedOwnershipDocumentFileName: undefined
            })
          }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>
          Evidence of Business Activity
          </InputLabel>
          <OptionalLabel>PDF</OptionalLabel>
          <Tooltip message='Provide a document that demonstrates your business activity, such as a recent invoice or contract.' />
        </div>
        <VerificationRequirementError message={getError('activityDocument')} />
        <div style={{
          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
          padding: 20,
          background: RADOM_COLORS.GRAY9,
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        }}>
          <span>Upload one of the following issued to the applying entity:</span>
          <ul style={{ paddingLeft: 30, margin: 0 }}>
            <li><b>Company invoices</b>(less than 90 days old)</li>
            <li><b>Sample contracts</b> or <b>merchant agreements</b></li>
          </ul>
        </div>
        <PDFFileUpload
          file={state.activityDocumentUrl ?? state.activityDocumentFile}
          onFile={f => onUpdate({ activityDocumentFile: f })}
          onRemove={() => {
            onUpdate({
              activityDocument: '',
              activityDocumentUrl: undefined,
              activityDocumentFile: undefined,
              lastUploadedActivityDocumentFileName: undefined
            })
          }} />
      </div></>
      : <><div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Formation or ownership document</InputLabel>
          <OptionalLabel>PDF</OptionalLabel>
          <Tooltip message='Upload your certificate of ownership or formation or incorporation' />
        </div>
        {getError('formationOrOwnershipDocument') && <VerificationRequirementError message={getError('formationOrOwnershipDocument')} />}

        <PDFFileUpload
          file={state.formationOrOwnershipDocumentUrl ?? state.formationOrOwnershipDocumentFile}
          onFile={f => onUpdate({ formationOrOwnershipDocumentFile: f })}
          onRemove={() => {
            onUpdate({
              formationOrOwnershipDocument: '',
              formationOrOwnershipDocumentUrl: undefined,
              formationOrOwnershipDocumentFile: undefined,
              lastUploadedFormationOrOwnershipDocumentFileName: undefined
            })
          }} />
      </div></>}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <InputLabel>
          Additional documents
        </InputLabel>
        <OptionalLabel>Optional</OptionalLabel>
        <OptionalLabel>PDF</OptionalLabel>

        <Tooltip message={`Provide documents that might support your application (up to ${MAX_ADDITIONAL_DOCUMENTS})`} />
      </div>
      <VerificationRequirementError message={getError('additionalDocument')} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        { additionalDocuments.map((doc, index) => (
          <PDFFileUpload
            key={index}
            file={doc.url ?? doc.fileObj}
            onFile={f => {
              const next = [...additionalDocuments]
              next[index] = { ...next[index], fileObj: f, fileName: f.name }
              onUpdate({ additionalDocuments: next })
            }}
            onRemove={() => {
              onUpdate({ additionalDocuments: additionalDocuments.filter((_, i) => i !== index) })
            }} />
        )) }

        { additionalDocuments.length < MAX_ADDITIONAL_DOCUMENTS && (
          <PDFFileUpload
            key={`new-${additionalDocuments.length}`}
            file={undefined}
            onFile={f => {
              onUpdate({ additionalDocuments: [...additionalDocuments, { fileObj: f, fileName: f.name }] })
            }}
            onRemove={() => {}} />
        ) }
      </div>
    </div>

    <PrimaryButton
      disabled={isSubmitting}
      isLoading={isSubmitting}
      onClick={() => {
        if (isValid) {
          onSubmit()
        } else {
          highlightInvalidFields()
        }
      }}>
      Continue
    </PrimaryButton>
  </div>
  )
}

export default Documents
